import { forwardRef, type TextareaHTMLAttributes, useCallback, useEffect, useRef } from 'react';
import { cn } from '@/utils/cn';

// ============================================
// TYPES
// ============================================

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  autoResize?: boolean;
}

// ============================================
// COMPONENT
// ============================================

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, autoResize, onChange, ...props }, ref) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    // Handle auto-resize
    const adjustHeight = useCallback(() => {
      const textarea = internalRef.current;
      if (textarea && autoResize) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [autoResize]);

    useEffect(() => {
      adjustHeight();
    }, [props.value, adjustHeight]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (autoResize) {
        adjustHeight();
      }
      onChange?.(e);
    };

    return (
      <textarea
        className={cn(
          'flex min-h-[100px] w-full rounded-md border bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)]',
          'placeholder:text-[var(--text-tertiary)] resize-none',
          'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent',
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[var(--bg-secondary)]',
          error ? 'border-red-500 focus:ring-red-500' : 'border-[var(--border-primary)]',
          className
        )}
        ref={(node) => {
          internalRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        onChange={handleChange}
        {...props}
      />
    );
  }
);

Textarea.displayName = 'Textarea';

// ============================================
// TEXTAREA WITH LABEL
// ============================================

interface TextareaWithLabelProps extends TextareaProps {
  label: string;
  id: string;
  helperText?: string;
  errorMessage?: string;
}

export function TextareaWithLabel({
  label,
  id,
  helperText,
  errorMessage,
  error,
  className,
  ...props
}: TextareaWithLabelProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={id} className="block text-sm font-medium text-[var(--text-secondary)]">
        {label}
      </label>
      <Textarea id={id} error={error ?? !!errorMessage} {...props} />
      {helperText && !errorMessage && (
        <p className="text-sm text-[var(--text-tertiary)]">{helperText}</p>
      )}
      {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
    </div>
  );
}
